// src/pages/PracticeMode.jsx
// Piano With Pan — Beginner Practice Mode v5.2
// ✅ V4: Progress tracking added — records session on unmount if >30 seconds
//
// ✅ MIC FIX: Single RealPianoListener instance (never remounts)
//    Mirrors LearnYourNotes.jsx architecture exactly:
//    - One listener with the full 16-note melody (all 10 questions concatenated)
//    - Mic stream stays alive across ALL question advances — no stop/restart
//    - Progress derived from a single globalMatched counter, just like FLAT_TARGET in LearnYourNotes
//
// ✅ All v5 visual features preserved:
//    Stats LEFT | note card + piano CENTRE | info RIGHT
//    Rainbow bouncing ball, key glow, note colours, finish modal

import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import RealPianoListener from "../components/RealPianoListener";
import VirtualPiano, { normalizeNoteName as vpNormalizeNoteName } from "../components/VirtualPiano";
import TrebleTownMapModal from "../components/TrebleTownMapModal";
import { recordSession } from "../utils/progressTracker"; // ✅ V4

// ✅ Tutorial completion flag
function setTutorialDone() {
  try { localStorage.setItem("pwp_tutorial_done_v1", "1"); } catch {}
}

/* ═══════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════ */
const WHITE_NOTES  = ["C","D","E","F","G","A","B"];
const FINAL_MELODY = ["C","C","G","G","A","A","G"];
const TOTAL_Q      = 10;
const MIC_ID       = "pm-mic-ctrl";

const NOTE_COL = {
  C:{ bg:"#FEE2E2", text:"#991B1B", border:"#FCA5A5", glow:"rgba(239,68,68,0.28)"   },
  D:{ bg:"#FEF3C7", text:"#92400E", border:"#FCD34D", glow:"rgba(245,158,11,0.28)"  },
  E:{ bg:"#D1FAE5", text:"#065F46", border:"#6EE7B7", glow:"rgba(52,211,153,0.28)"  },
  F:{ bg:"#DBEAFE", text:"#1E40AF", border:"#93C5FD", glow:"rgba(96,165,250,0.28)"  },
  G:{ bg:"#EDE9FE", text:"#5B21B6", border:"#C4B5FD", glow:"rgba(167,139,250,0.28)" },
  A:{ bg:"#FCE7F3", text:"#9D174D", border:"#F9A8D4", glow:"rgba(244,114,182,0.28)" },
  B:{ bg:"#FFEDD5", text:"#9A3412", border:"#FDBA74", glow:"rgba(251,146,60,0.28)"  },
};

const MIC_CFG = {
  detectionSensitivity:1.25, toleranceCents:85, toleranceHz:14,
  wrongStableFrames:1, wrongCooldownMs:180, wrongCentsThreshold:70, wrongHzThreshold:7,
};

/* ═══════════════════════════════════════════════
   NOTE HELPERS
═══════════════════════════════════════════════ */
const FLAT_TO_SHARP = { DB:"C#",EB:"D#",GB:"F#",AB:"G#",BB:"A#",CB:"B",FB:"E" };
const EDGE_ENH      = { "E#":"F","B#":"C" };
const MIDI_PC       = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];
const NOTE_FREQ     = { C:261.63,D:293.66,E:329.63,F:349.23,G:392.0,A:440.0,B:493.88 };

function normSafe(n) {
  if (!n) return "";
  let r = String(n).trim().toUpperCase().replaceAll("♯","#").replaceAll("♭","B");
  const m = r.match(/[A-G](#|B)?/);
  if (!m) return "";
  let pc = m[0];
  if (EDGE_ENH[pc]) pc = EDGE_ENH[pc];
  if (pc.length===2&&pc[1]==="B") pc = FLAT_TO_SHARP[pc]||pc;
  return vpNormalizeNoteName ? vpNormalizeNoteName(pc) : pc;
}
function normAny(x) {
  if (x==null) return "";
  if (Array.isArray(x)) return normAny(x[x.length-1]??x[0]);
  if (typeof x==="number"&&Number.isFinite(x)) return normSafe(MIDI_PC[((Math.round(x)%12)+12)%12]);
  if (typeof x==="object"&&x.name) return normSafe(x.name);
  return normSafe(String(x).split(/[/,|]/)[0]);
}
function previewNote(n) {
  try {
    const freq = NOTE_FREQ[normSafe(n)]; if (!freq) return;
    const ac=new(window.AudioContext||window.webkitAudioContext)();
    const osc=ac.createOscillator(), g=ac.createGain();
    osc.type="triangle"; osc.frequency.value=freq;
    g.gain.setValueAtTime(0.38,ac.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001,ac.currentTime+1.1);
    osc.connect(g).connect(ac.destination); osc.start(); osc.stop(ac.currentTime+1.1);
  } catch {}
}

/* ✅ Build questions once — returns both question array AND flat melody */
function buildQuestionsAndMelody() {
  const nine = Array.from({length:9}, (_,i) => ({
    id:`q${i+1}`, type:"single",
    seq:[ WHITE_NOTES[Math.floor(Math.random()*7)] ],
  }));
  const questions = [...nine, {id:"q10", type:"melody", seq:FINAL_MELODY}];
  const fullMelody = questions.flatMap(q => q.seq);
  return { questions, fullMelody };
}

/* Derive qIdx + within-question matched from global note count */
function deriveProgress(questions, globalMatched) {
  const total = questions.reduce((s,q)=>s+q.seq.length, 0);
  const clamped = Math.max(0, Math.min(globalMatched, total));
  let remaining = clamped;
  for (let i=0; i<questions.length; i++) {
    const len = questions[i].seq.length;
    if (remaining < len) return { qIdx:i, matched:remaining };
    remaining -= len;
  }
  return { qIdx:questions.length, matched:0 };
}

/* ═══════════════════════════════════════════════
   BOUNCING BALL
═══════════════════════════════════════════════ */
function BouncingBall({ targetNote, wrapperRef }) {
  const [pos,setPos]=useState(null);
  const recalc=useCallback(()=>{
    if (!targetNote||!wrapperRef?.current){setPos(null);return;}
    const wrap=wrapperRef.current, wR=wrap.getBoundingClientRect();
    let el=wrap.querySelector(`[data-note="${targetNote}"]`);
    if (!el){const pc=normAny(targetNote);if(pc)el=wrap.querySelector(`[data-note^="${pc}"]`);}
    if (!el){setPos(null);return;}
    const kR=el.getBoundingClientRect();
    setPos({cx:kR.left-wR.left+kR.width/2, cy:kR.top-wR.top-58});
  },[targetNote,wrapperRef]);
  useEffect(()=>{recalc();const t=setTimeout(recalc,160);return()=>clearTimeout(t);},[recalc]);
  useEffect(()=>{
    const fn=()=>setTimeout(recalc,100);
    window.addEventListener("resize",fn);
    return()=>window.removeEventListener("resize",fn);
  },[recalc]);
  if (!pos) return null;
  const D=38;
  return (
    <div style={{position:"absolute",left:pos.cx-D/2,top:pos.cy,width:D,height:D,
      borderRadius:"50%",
      background:"conic-gradient(from 0deg,#FF6B6B,#FFE66D,#6BCB77,#4D96FF,#FF6B6B)",
      boxShadow:"0 6px 18px rgba(0,0,0,0.22),inset 0 -3px 6px rgba(0,0,0,0.12),inset 0 3px 8px rgba(255,255,255,0.45)",
      animation:"pmBounce 0.52s ease-in-out infinite alternate",
      pointerEvents:"none",zIndex:30}}>
      <div style={{position:"absolute",top:5,left:8,width:10,height:6,borderRadius:"50%",
        background:"rgba(255,255,255,0.68)",transform:"rotate(-35deg)"}}/>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   NOTE CARD
═══════════════════════════════════════════════ */
function NoteCard({ note, flashState, onHear, isFinal, seq, matched }) {
  const col=NOTE_COL[note]||NOTE_COL.C;
  const bg    =flashState==="correct"?"#DCFCE7":flashState==="wrong"?"#FEE2E2":col.bg;
  const border=flashState==="correct"?"#4ADE80":flashState==="wrong"?"#F87171":col.border;
  const shadow=flashState==="correct"?"0 10px 40px rgba(34,197,94,0.40)":
               flashState==="wrong"  ?"0 10px 40px rgba(239,68,68,0.40)":
               `0 10px 40px ${col.glow}`;
  const nc    =flashState==="correct"?"#16A34A":flashState==="wrong"?"#DC2626":col.text;
  return (
    <div style={{background:bg,border:`2.5px solid ${border}`,borderRadius:28,
      padding:"14px 28px 12px",display:"flex",flexDirection:"column",
      alignItems:"center",gap:8,boxShadow:shadow,
      transition:"background .2s,border-color .2s,box-shadow .2s",
      animation:flashState==="wrong"?"pmShake .28s ease":
                flashState==="correct"?"pmPop .30s cubic-bezier(.34,1.56,.64,1)":"none",
      position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",top:-20,right:-20,width:70,height:70,borderRadius:"50%",
        background:"rgba(255,255,255,0.32)",pointerEvents:"none"}}/>
      <div style={{position:"absolute",bottom:-12,left:-12,width:50,height:50,borderRadius:"50%",
        background:"rgba(255,255,255,0.22)",pointerEvents:"none"}}/>
      <div style={{fontSize:"0.62rem",fontWeight:800,textTransform:"uppercase",
        letterSpacing:"0.18em",color:nc,opacity:0.65,position:"relative",zIndex:1}}>
        {flashState==="correct"?"Correct!":isFinal?"Doron's Sleep Spell":"Find this note"}
      </div>
      <div style={{display:"flex",alignItems:"center",gap:16,position:"relative",zIndex:1}}>
        <div style={{fontFamily:"'Fredoka One',cursive",fontSize:"clamp(3rem,8vw,5rem)",
          color:nc,lineHeight:1,
          textShadow:flashState==="idle"?`0 4px 16px ${col.border}`:"none",
          animation:flashState==="idle"?"pmFloat 3s ease-in-out infinite":"none",
          transition:"color .2s"}}>
          {flashState==="correct"?"✓":note}
        </div>
        {isFinal&&(
          <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
            {seq.map((n,i)=>{
              const done=i<matched,cur=i===matched,c=NOTE_COL[n]||NOTE_COL.C;
              return(
                <div key={i} style={{width:32,height:36,borderRadius:10,
                  background:done?"#DCFCE7":cur?c.bg:"#F8FAFC",
                  border:`2px solid ${done?"#86EFAC":cur?c.border:"#E2E8F0"}`,
                  display:"flex",alignItems:"center",justifyContent:"center",
                  boxShadow:cur?`0 0 0 3px ${c.border}44`:"none",
                  transform:cur&&flashState==="correct"?"scale(1.1)":"scale(1)",
                  transition:"all .22s cubic-bezier(.34,1.56,.64,1)"}}>
                  <span style={{fontFamily:"'Fredoka One',cursive",fontSize:"0.9rem",
                    color:done?"#16A34A":cur?c.text:"#CBD5E1"}}>{done?"✓":n}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {flashState==="idle"&&(
        <button type="button" onClick={()=>onHear(note)} style={{
          background:"rgba(255,255,255,0.72)",border:`2px solid ${col.border}`,
          borderRadius:100,padding:"5px 16px",fontFamily:"'Nunito',sans-serif",
          fontWeight:800,fontSize:"0.76rem",color:col.text,cursor:"pointer",
          display:"flex",alignItems:"center",gap:6,position:"relative",zIndex:1}}
          onMouseDown={e=>{e.currentTarget.style.transform="scale(0.93)";}}
          onMouseUp={e=>{e.currentTarget.style.transform="scale(1)";}}>
          🔊 Hear it
        </button>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SIDE CARDS
═══════════════════════════════════════════════ */
function SideCard({ children, style={} }) {
  return (
    <div style={{background:"rgba(255,255,255,0.92)",borderRadius:20,
      padding:"14px 16px",boxShadow:"0 3px 14px rgba(0,0,0,0.07)",...style}}>
      {children}
    </div>
  );
}
function StatCard({ emoji, label, value, color, glow }) {
  return (
    <SideCard style={{boxShadow:glow?`0 4px 20px ${glow},0 2px 8px rgba(0,0,0,0.05)`:"0 3px 14px rgba(0,0,0,0.07)"}}>
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        <span style={{fontSize:"1.7rem",lineHeight:1}}>{emoji}</span>
        <div>
          <div style={{fontFamily:"'Fredoka One',cursive",fontSize:"1.6rem",color,lineHeight:1}}>{value}</div>
          <div style={{fontSize:"0.6rem",fontWeight:800,textTransform:"uppercase",
            letterSpacing:"0.14em",color:"#94A3B8",marginTop:2}}>{label}</div>
        </div>
      </div>
    </SideCard>
  );
}
function ProgressCard({ doneSet, qIdx }) {
  return (
    <SideCard>
      <div style={{fontSize:"0.6rem",fontWeight:800,textTransform:"uppercase",
        letterSpacing:"0.14em",color:"#94A3B8",marginBottom:10,textAlign:"center"}}>
        Progress
      </div>
      <div style={{display:"flex",gap:5,justifyContent:"center",flexWrap:"wrap"}}>
        {Array.from({length:TOTAL_Q},(_,i)=>{
          const done=doneSet.has(i), cur=i===qIdx;
          return(
            <div key={i} style={{width:cur?22:13,height:13,borderRadius:100,
              background:done?"linear-gradient(90deg,#34D399,#10B981)":
                          cur?"linear-gradient(90deg,#60A5FA,#3B82F6)":"#E2E8F0",
              boxShadow:cur?"0 0 0 3px rgba(96,165,250,0.32)":"none",
              transition:"all .32s cubic-bezier(.34,1.56,.64,1)"}}/>
          );
        })}
      </div>
    </SideCard>
  );
}

/* ═══════════════════════════════════════════════
   FINISH MODAL
═══════════════════════════════════════════════ */
function FinishModal({ open, score, accuracy, onClose, goLevel1, again, learnNotes, onShowMap }) {
  if (!open) return null;
  return (
    <div style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,0.55)",
      backdropFilter:"blur(6px)",display:"flex",alignItems:"center",
      justifyContent:"center",padding:16}}>
      <div style={{background:"white",borderRadius:34,padding:"36px 24px 28px",
        maxWidth:440,width:"100%",boxShadow:"0 28px 70px rgba(0,0,0,0.26)",
        textAlign:"center",position:"relative",
        animation:"pmPop .4s cubic-bezier(.34,1.56,.64,1)"}}>
        <button type="button" onClick={onClose} style={{position:"absolute",top:14,right:16,
          background:"none",border:"none",fontSize:"1rem",cursor:"pointer",color:"#CBD5E1"}}>✕</button>
        <div style={{fontSize:"3rem",marginBottom:4}}>🎉</div>
        <div style={{fontFamily:"'Fredoka One',cursive",fontSize:"1.8rem",color:"#1E293B"}}>
          Amazing Work!
        </div>
        <div style={{fontWeight:700,color:"#64748B",marginTop:5,fontSize:"0.88rem"}}>
          You finished Beginner Practice Mode
        </div>
        <div style={{display:"flex",gap:12,justifyContent:"center",margin:"18px 0 20px"}}>
          {[{l:"Score",v:score,bg:"#F0FDF4",c:"#16A34A"},
            {l:"Accuracy",v:`${accuracy}%`,bg:"#EFF6FF",c:"#2563EB"}].map(({l,v,bg,c})=>(
            <div key={l} style={{flex:1,background:bg,borderRadius:16,padding:"12px 10px"}}>
              <div style={{fontFamily:"'Fredoka One',cursive",fontSize:"1.7rem",color:c}}>{v}</div>
              <div style={{fontSize:"0.62rem",fontWeight:800,textTransform:"uppercase",
                letterSpacing:"0.12em",color:c,opacity:0.6,marginTop:2}}>{l}</div>
            </div>
          ))}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <button type="button" onClick={onShowMap} style={{padding:"14px 24px",borderRadius:100,
            background:"linear-gradient(135deg,#059669,#047857)",color:"white",
            fontFamily:"'Fredoka One',cursive",fontSize:"1rem",border:"none",cursor:"pointer",
            boxShadow:"0 7px 0 #065F46,0 9px 22px rgba(5,150,105,0.32)"}}>
            🗺️ See Your Adventure Map
          </button>
          <button type="button" onClick={goLevel1} style={{padding:"14px 24px",borderRadius:100,
            background:"linear-gradient(135deg,#7C3AED,#6D28D9)",color:"white",
            fontFamily:"'Fredoka One',cursive",fontSize:"1rem",border:"none",cursor:"pointer",
            boxShadow:"0 7px 0 #5B21B6,0 9px 22px rgba(109,40,217,0.32)"}}>
            🐉 Go to Level 1
          </button>
          <div style={{display:"flex",gap:9}}>
            <button type="button" onClick={again} style={{flex:1,padding:"11px",borderRadius:100,
              background:"white",border:"2px solid #E2E8F0",fontWeight:800,
              fontSize:"0.82rem",cursor:"pointer",color:"#334155"}}>
              Practice Again
            </button>
            <button type="button" onClick={learnNotes} style={{flex:1,padding:"11px",borderRadius:100,
              background:"#F0F9FF",border:"2px solid #BAE6FD",fontWeight:800,
              fontSize:"0.82rem",cursor:"pointer",color:"#0369A1"}}>
              Learn Notes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════ */
export default function PracticeMode() {
  const go = useNavigate();
  const pianoWrapRef = useRef(null);

  // ✅ V4: Track session duration for progress reporting
  const practiceStartRef = useRef(Date.now());

  useEffect(() => {
    practiceStartRef.current = Date.now();
    return () => {
      const ms = Date.now() - practiceStartRef.current;
      if (ms > 30_000) {
        try {
          recordSession({
            source:       "practice",
            levelId:      "free",
            levelName:    "Practice Mode",
            totalNotes:   0,
            correctNotes: 0,
            wrongNotes:   0,
            durationMs:   ms,
            stars:        null,
          });
        } catch {}
      }
    };
  }, []); // ✅ empty dep array — runs once on mount, records on unmount

  /* Window width for responsive layout */
  const [wW,setWW]=useState(typeof window!=="undefined"?window.innerWidth:1024);
  useEffect(()=>{
    let raf=0;
    const fn=()=>{cancelAnimationFrame(raf);raf=requestAnimationFrame(()=>setWW(window.innerWidth));};
    window.addEventListener("resize",fn);
    return()=>{cancelAnimationFrame(raf);window.removeEventListener("resize",fn);};
  },[]);
  const isMobile  = wW < 640;
  const isTablet  = wW >= 640 && wW < 1024;
  const isDesktop = wW >= 1024;

  /* Piano sizing */
  const pianoCfg = useMemo(()=>{
    if (isDesktop) return { kw:64, wh:210, bkw:38, bkh:132 };
    if (isTablet) {
      const kw=Math.min(Math.floor((wW-240)/8),54);
      const r=kw/54;
      return { kw,wh:Math.round(180*r),bkw:Math.round(32*r),bkh:Math.round(114*r) };
    }
    const kw=Math.floor((wW-24)/8);
    const r=kw/40;
    return { kw,wh:Math.round(148*r),bkw:Math.round(26*r),bkh:Math.round(94*r) };
  },[isDesktop,isTablet,wW]);

  /* ✅ Build questions + fullMelody ONCE */
  const [{ questions, fullMelody }] = useState(()=> buildQuestionsAndMelody());
  const TOTAL_NOTES = fullMelody.length;

  /* ✅ Single global note counter */
  const [globalMatched, setGlobalMatched] = useState(0);
  const allDone = globalMatched >= TOTAL_NOTES;

  /* Derive current question index + within-question progress from globalMatched */
  const { qIdx, matched } = useMemo(()=>
    deriveProgress(questions, globalMatched),
  [questions, globalMatched]);

  /* Derived state */
  const curQ    = questions[qIdx] || questions[TOTAL_Q-1];
  const seq     = curQ?.seq || ["C"];
  const isFinal = qIdx === TOTAL_Q - 1;
  const expected = !allDone ? seq[Math.min(matched, seq.length-1)] : null;

  /* Track completed questions for progress dots */
  const doneSet = useMemo(()=>{
    const s = new Set();
    let rem = globalMatched;
    for (let i=0; i<questions.length; i++) {
      const len = questions[i].seq.length;
      if (rem >= len) { s.add(i); rem -= len; }
      else break;
    }
    return s;
  },[globalMatched, questions]);

  /* Score / streak / stats */
  const [score,    setScore   ]=useState(0);
  const [streak,   setStreak  ]=useState(0);
  const [best,     setBest    ]=useState(0);
  const [attempts, setAttempts]=useState(0);
  const [corrects, setCorrects]=useState(0);
  const wrongCount = attempts - corrects;
  const accuracy   = attempts===0?100:Math.round((corrects/attempts)*100);

  /* UI state */
  const [flash,    setFlash   ]=useState("idle");
  const [started,  setStarted ]=useState(false);
  const [listening,setListening]=useState(false);
  const [showEnd,  setShowEnd ]=useState(false);
  const [showMap,  setShowMap  ]=useState(false);
  const [toast,    setToast   ]=useState("");

  const flashT = useRef(null);
  const toastT = useRef(null);
  const wrongGate = useRef({at:0,pc:null,n:0});

  useEffect(()=>()=>{
    if(flashT.current)clearTimeout(flashT.current);
    if(toastT.current)clearTimeout(toastT.current);
  },[]);

  /* Show finish modal when all notes matched */
  useEffect(()=>{
    if (allDone && !showEnd) {
      try{ const b=document.querySelector(`#${MIC_ID} button`); if(b)b.click(); }catch{}
      setListening(false);
      setShowEnd(true);
    }
  },[allDone, showEnd]);

  const trigFlash=useCallback((kind)=>{
    if(flashT.current)clearTimeout(flashT.current);
    setFlash(kind);
    flashT.current=setTimeout(()=>setFlash("idle"),kind==="correct"?500:290);
  },[]);

  const showToast=useCallback((msg)=>{
    setToast(msg);
    if(toastT.current)clearTimeout(toastT.current);
    toastT.current=setTimeout(()=>setToast(""),2200);
  },[]);

  /* Key glow */
  useEffect(()=>{
    if(typeof document==="undefined") return;
    document.querySelectorAll(".tt-current-key-glow")
      .forEach(el=>el.classList.remove("tt-current-key-glow"));
    if(!expected||allDone) return;
    let els=Array.from(document.querySelectorAll(`[data-note="${expected}"]`));
    if(!els.length){
      const pc=normAny(expected);
      if(pc)els=Array.from(document.querySelectorAll(`[data-note^="${pc}"]`));
    }
    els.forEach(el=>el.classList.add("tt-current-key-glow"));
  },[expected,allDone,qIdx,matched]);

  /* Correct answer handler */
  const onCorrect=useCallback(()=>{
    setAttempts(a=>a+1);setCorrects(a=>a+1);
    setScore(s=>s+10+Math.min(streak*2,20));
    setStreak(s=>{const ns=s+1;setBest(b=>Math.max(b,ns));return ns;});
    trigFlash("correct");
    setGlobalMatched(prev=>Math.min(TOTAL_NOTES, prev+1));
  },[streak,trigFlash,TOTAL_NOTES]);

  const onWrong=useCallback(()=>{
    setAttempts(a=>a+1);setStreak(0);trigFlash("wrong");
  },[trigFlash]);

  /* Mic toggle */
  const toggleMic=useCallback(()=>{
    const btn=document.querySelector(`#${MIC_ID} button`);
    if(btn){btn.click();setListening(p=>!p);}
  },[]);

  /* Virtual piano click */
  const onKey=useCallback((noteName)=>{
    if(allDone)return;
    if(!started)setStarted(true);
    if(listening){toggleMic();showToast("Switched to virtual piano — mic off");}
    const played=normAny(noteName), exp=expected?normAny(expected):"";
    if(played&&exp){ played===exp?onCorrect():onWrong(); }
  },[allDone,started,listening,toggleMic,showToast,expected,onCorrect,onWrong]);

  /* Mic callbacks */
  const onMicDetected=useCallback((note)=>{
    const pc=normAny(note);
    if(!pc||allDone||!listening)return;
    const exp=expected?normAny(expected):"";
    if(!exp||pc===exp){wrongGate.current={at:0,pc:null,n:0};return;}
    const now=Date.now(),g=wrongGate.current;
    if(g.pc!==pc){wrongGate.current={at:now,pc,n:1};return;}
    const n=(g.n||0)+1; wrongGate.current={at:now,pc,n};
    if(n>=2&&now-g.at>=220){onWrong();wrongGate.current={at:now,pc,n:0};}
  },[allDone,listening,expected,onWrong]);

  const onMicMatched=useCallback((arr)=>{
    if(!Array.isArray(arr)||allDone)return;
    const len=arr.length;
    setGlobalMatched(prev=>{
      if(len<=prev)return prev;
      const newNotes=len-prev;
      setAttempts(a=>a+newNotes);setCorrects(a=>a+newNotes);
      setScore(s=>s+newNotes*(10+Math.min(streak*2,20)));
      setStreak(s=>{const ns=s+newNotes;setBest(b=>Math.max(b,ns));return ns;});
      trigFlash("correct");
      wrongGate.current={at:0,pc:null,n:0};
      return len;
    });
  },[allDone,streak,trigFlash]);

  /* Reset */
  const reset=useCallback(()=>{
    if(listening){
      try{const b=document.querySelector(`#${MIC_ID} button`);if(b)b.click();}catch{}
    }
    setListening(false);setStarted(false);setShowEnd(false);
    setGlobalMatched(0);
    setScore(0);setStreak(0);setBest(0);setAttempts(0);setCorrects(0);
    setFlash("idle");setToast("");
    wrongGate.current={at:0,pc:null,n:0};
    // ✅ V4: Reset session start timer on restart so duration is accurate
    practiceStartRef.current = Date.now();
  },[listening]);

  /* ──────────────────────────────────────────
     PIANO + CONTROLS BLOCK
  ────────────────────────────────────────── */
  const PianoBlock = (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:0,width:"100%"}}>

      {!allDone&&(
        <div style={{marginBottom:12,width:"100%",display:"flex",justifyContent:"center"}}>
          <NoteCard note={expected||"C"} flashState={flash}
            onHear={previewNote} isFinal={isFinal}
            seq={FINAL_MELODY} matched={matched}/>
        </div>
      )}

      {!allDone&&(
        <div style={{marginBottom:10,display:"flex",justifyContent:"center"}}>
          {!started?(
            <button type="button" onClick={()=>{setStarted(true);toggleMic();}} style={{
              padding:"10px 28px",borderRadius:100,
              background:"linear-gradient(135deg,#22C55E,#16A34A)",
              color:"white",fontFamily:"'Fredoka One',cursive",fontSize:"0.95rem",
              border:"none",cursor:"pointer",
              boxShadow:"0 6px 0 #15803D,0 8px 18px rgba(34,197,94,0.32)"}}>
              🎤 Use Real Piano
            </button>
          ):(
            <button type="button" onClick={toggleMic} style={{
              padding:"8px 22px",borderRadius:100,
              background:listening?"#ECFDF5":"rgba(255,255,255,0.9)",
              border:`2px solid ${listening?"#86EFAC":"#E2E8F0"}`,
              color:listening?"#166534":"#64748B",
              fontWeight:800,fontSize:"0.78rem",cursor:"pointer",
              display:"flex",alignItems:"center",gap:7,transition:"all .2s"}}>
              {listening?"🎤 Listening…":"🎤 Enable Mic"}
            </button>
          )}
        </div>
      )}

      {!allDone&&(
        <div style={{fontSize:"0.58rem",fontWeight:800,textTransform:"uppercase",
          letterSpacing:"0.18em",color:"#CBD5E1",marginBottom:10,textAlign:"center"}}>
          — or click a key —
        </div>
      )}

      <div ref={pianoWrapRef} style={{
        position:"relative",display:"flex",justifyContent:"center",
        overflow:"visible",
        background:"rgba(255,255,255,0.80)",borderRadius:24,
        padding:`60px ${isDesktop?28:16}px ${isDesktop?24:18}px`,
        boxShadow:"0 14px 45px rgba(0,0,0,0.07),inset 0 1px 0 rgba(255,255,255,0.9)",
        border:"1.5px solid rgba(255,255,255,0.9)",width:"100%"}}>
        {expected&&!allDone&&<BouncingBall targetNote={expected} wrapperRef={pianoWrapRef}/>}
        <VirtualPiano onNoteClick={onKey}
          keyWidth={pianoCfg.kw} whiteKeyHeight={pianoCfg.wh}
          blackKeyWidth={pianoCfg.bkw} blackKeyHeight={pianoCfg.bkh}
          firstKey="C4" lastKey="C5" showAllBlackLabels={false}/>
      </div>

      <div style={{display:"flex",gap:22,justifyContent:"center",marginTop:10}}>
        <button type="button" onClick={reset} style={{background:"none",border:"none",
          cursor:"pointer",fontWeight:800,fontSize:"0.72rem",color:"#94A3B8",
          textDecoration:"underline",textDecorationStyle:"dashed",textUnderlineOffset:3}}>
          Restart
        </button>
        <button type="button" onClick={()=>go("/learn-your-notes")} style={{background:"none",
          border:"none",cursor:"pointer",fontWeight:800,fontSize:"0.72rem",color:"#94A3B8",
          textDecoration:"underline",textDecorationStyle:"dashed",textUnderlineOffset:3}}>
          ← Learn Notes
        </button>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════ */
  return (
    <div style={{minHeight:"100dvh",
      background:"linear-gradient(155deg,#FFF7ED 0%,#EFF6FF 55%,#F0FDF4 100%)",
      fontFamily:"'Nunito',sans-serif",display:"flex",flexDirection:"column"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@600;700;800;900&display=swap');
        .tt-current-key-glow {
          box-shadow: 0 0 0 4px rgba(59,130,246,0.50), 0 0 22px rgba(59,130,246,0.65) !important;
          transform: translateY(-2px); transition: box-shadow .15s ease, transform .15s ease;
          border-radius: 10px; z-index: 5 !important;
        }
        @keyframes pmBounce { from{transform:translateY(0) scale(1)} to{transform:translateY(-22px) scale(1.07)} }
        @keyframes pmFloat  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }
        @keyframes pmPop    { from{transform:scale(0.5);opacity:0} to{transform:scale(1);opacity:1} }
        @keyframes pmShake  {
          0%,100%{transform:translateX(0)} 20%{transform:translateX(-9px)}
          40%{transform:translateX(9px)} 60%{transform:translateX(-6px)} 80%{transform:translateX(6px)}
        }
        @keyframes pmBlob { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-14px) scale(1.04)} }
        button { -webkit-tap-highlight-color:transparent; }
      `}</style>

      {/* ✅ Skip banner */}
      <div style={{
        position:"relative",zIndex:50,
        background:"rgba(255,255,255,0.92)",
        borderBottom:"1.5px solid #e0f2fe",
        backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)",
        padding:"8px 16px",
        display:"flex",alignItems:"center",justifyContent:"space-between",
        gap:8,flexWrap:"wrap",
      }}>
        <span style={{fontSize:"0.75rem",fontWeight:800,color:"#64748b",fontFamily:"'Nunito',sans-serif"}}>
          🎹 Practice Mode
        </span>
        <button type="button" onClick={()=>{ setTutorialDone(); go("/game-menu"); }} style={{
          background:"none",border:"1.5px solid #cbd5e1",borderRadius:100,padding:"4px 14px",
          fontSize:"0.72rem",fontWeight:900,color:"#059669",cursor:"pointer",
          fontFamily:"'Nunito',sans-serif",letterSpacing:"0.04em",whiteSpace:"nowrap",
        }}>
          Ready to play? Skip to Level 1 →
        </button>
      </div>

      {/* Background blobs */}
      <div style={{position:"fixed",top:-80,right:-80,width:320,height:320,borderRadius:"50%",
        background:"radial-gradient(circle,rgba(196,181,253,0.48),transparent 70%)",
        animation:"pmBlob 7s ease-in-out infinite",pointerEvents:"none",zIndex:0}}/>
      <div style={{position:"fixed",bottom:-60,left:-60,width:260,height:260,borderRadius:"50%",
        background:"radial-gradient(circle,rgba(167,243,208,0.50),transparent 70%)",
        animation:"pmBlob 9s ease-in-out infinite reverse",pointerEvents:"none",zIndex:0}}/>

      {/* NAV */}
      <header style={{position:"relative",zIndex:10,
        background:"rgba(255,255,255,0.88)",backdropFilter:"blur(14px)",
        borderBottom:"1px solid rgba(0,0,0,0.07)",
        flexShrink:0,height:46,display:"flex",alignItems:"center"}}>
        <div style={{maxWidth:1400,margin:"0 auto",width:"100%",
          padding:"0 16px",display:"flex",alignItems:"center"}}>
          <button type="button" onClick={()=>go("/home")} style={{
            fontFamily:"'Fredoka One',cursive",fontSize:"1.2rem",color:"#1E40AF",
            background:"none",border:"none",cursor:"pointer"}}>Piano With Pan</button>
          <div style={{position:"absolute",left:"50%",transform:"translateX(-50%)",
            fontFamily:"'Fredoka One',cursive",
            fontSize:isMobile?"0.85rem":"1.05rem",
            color:"#64748B",whiteSpace:"nowrap",letterSpacing:"0.02em"}}>
            {allDone?"🎉 Practice Complete!":isFinal?"🐉 Twinkle Challenge":"🎹 Find the Note"}
          </div>
          <div style={{marginLeft:"auto",display:"flex",gap:14}}>
            {!isMobile&&[["Pricing","/pricing"],["Parents","/parents"],["About","/about"]].map(([l,p])=>(
              <button key={l} onClick={()=>go(p)} style={{background:"none",border:"none",
                cursor:"pointer",fontWeight:700,fontSize:"0.68rem",color:"#475569",
                letterSpacing:"0.1em"}}>{l.toUpperCase()}</button>
            ))}
          </div>
        </div>
      </header>

      {/* Toast */}
      {toast&&(
        <div style={{position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",
          zIndex:9999,background:"#1E293B",color:"white",
          padding:"7px 20px",borderRadius:100,fontWeight:700,fontSize:"0.75rem",
          boxShadow:"0 5px 20px rgba(0,0,0,0.26)"}}>
          {toast}
        </div>
      )}

      {/* CONTENT */}
      <div style={{flex:1,position:"relative",zIndex:1,
        padding:isMobile?"6px 10px 24px":"10px 16px 28px"}}>

        {/* MOBILE */}
        {isMobile&&(
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            <div style={{display:"flex",gap:5,justifyContent:"center",flexWrap:"wrap",flexShrink:0}}>
              {[
                ["🔥",streak,"#F59E0B",streak>=3?"rgba(245,158,11,0.35)":null],
                ["🎯",`${accuracy}%`,"#3B82F6",null],
                ["⭐",score,"#8B5CF6",null],
                ["❌",wrongCount,"#EF4444",null],
              ].map(([e,v,c,glow],i)=>(
                <div key={i} style={{background:"rgba(255,255,255,0.92)",borderRadius:12,
                  padding:"4px 10px",display:"flex",alignItems:"center",gap:5,
                  boxShadow:glow?`0 3px 12px ${glow}`:"0 1px 6px rgba(0,0,0,0.07)"}}>
                  <span style={{fontSize:"0.9rem"}}>{e}</span>
                  <span style={{fontFamily:"'Fredoka One',cursive",fontSize:"0.9rem",color:c}}>{v}</span>
                </div>
              ))}
              <div style={{display:"flex",gap:3,alignItems:"center",
                background:"rgba(255,255,255,0.92)",borderRadius:12,padding:"4px 8px",
                boxShadow:"0 1px 6px rgba(0,0,0,0.07)"}}>
                {Array.from({length:TOTAL_Q},(_,i)=>{
                  const done=doneSet.has(i),cur=i===qIdx;
                  return(
                    <div key={i} style={{width:cur?14:7,height:7,borderRadius:100,
                      background:done?"#34D399":cur?"#3B82F6":"#E2E8F0",
                      transition:"all .3s cubic-bezier(.34,1.56,.64,1)"}}/>
                  );
                })}
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column"}}>
              {PianoBlock}
            </div>
          </div>
        )}

        {/* TABLET / DESKTOP */}
        {!isMobile&&(
          <div style={{display:"grid",
            gridTemplateColumns:isDesktop?"190px 1fr 190px":"160px 1fr 160px",
            gap:isDesktop?20:14,alignItems:"start"}}>

            {/* LEFT */}
            <div style={{display:"flex",flexDirection:"column",gap:10,paddingTop:4}}>
              <StatCard emoji="🔥" label="Streak"      value={streak}
                color="#F59E0B" glow={streak>=3?"rgba(245,158,11,0.40)":null}/>
              <StatCard emoji="🎯" label="Accuracy"    value={`${accuracy}%`} color="#3B82F6"/>
              <StatCard emoji="⭐" label="Score"       value={score}          color="#8B5CF6"/>
              <StatCard emoji="🏆" label="Best Streak" value={best}           color="#D97706"/>
              <ProgressCard doneSet={doneSet} qIdx={qIdx}/>
            </div>

            {/* CENTRE */}
            <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
              {PianoBlock}
            </div>

            {/* RIGHT */}
            <div style={{display:"flex",flexDirection:"column",gap:10,paddingTop:4}}>
              <SideCard>
                <div style={{textAlign:"center"}}>
                  <div style={{fontFamily:"'Fredoka One',cursive",fontSize:"2.2rem",
                    color:"#3B82F6",lineHeight:1}}>
                    {Math.min(qIdx+1,TOTAL_Q)}
                    <span style={{fontSize:"1rem",color:"#CBD5E1"}}>/{TOTAL_Q}</span>
                  </div>
                  <div style={{fontSize:"0.6rem",fontWeight:800,textTransform:"uppercase",
                    letterSpacing:"0.14em",color:"#94A3B8",marginTop:3}}>Question</div>
                </div>
              </SideCard>
              <StatCard emoji="✅" label="Correct" value={corrects}   color="#10B981"/>
              <StatCard emoji="❌" label="Wrong"   value={wrongCount} color="#EF4444"/>
              <SideCard>
                <div style={{fontSize:"0.6rem",fontWeight:800,textTransform:"uppercase",
                  letterSpacing:"0.14em",color:"#94A3B8",marginBottom:8,textAlign:"center"}}>
                  White Keys
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:4,justifyContent:"center"}}>
                  {WHITE_NOTES.map(n=>{
                    const c=NOTE_COL[n];
                    return(
                      <div key={n} style={{width:30,height:30,borderRadius:9,
                        background:c.bg,border:`1.5px solid ${c.border}`,
                        display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <span style={{fontFamily:"'Fredoka One',cursive",
                          fontSize:"0.85rem",color:c.text}}>{n}</span>
                      </div>
                    );
                  })}
                </div>
              </SideCard>
              <SideCard style={{background:"linear-gradient(135deg,#EDE9FE,#DDD6FE)"}}>
                <div style={{fontSize:"0.62rem",fontWeight:800,color:"#5B21B6",marginBottom:4}}>
                  💡 Tip
                </div>
                <div style={{fontSize:"0.7rem",fontWeight:700,color:"#4C1D95",lineHeight:1.4}}>
                  {isFinal
                    ? "This is the first line of Twinkle Twinkle — the Sleep Spell for Doron! 🐉"
                    : "Look for the glowing blue key — that's the one to press!"}
                </div>
              </SideCard>
            </div>
          </div>
        )}
      </div>

      {/* ✅ SINGLE RealPianoListener — NO key prop, NEVER remounts */}
      <div id={MIC_ID} style={{display:"none"}}>
        <RealPianoListener
          targetMelody={fullMelody}
          startLabel=" " stopLabel=" "
          showMicHint={false}
          buttonWrapperClassName="hidden"
          containerClassName="hidden"
          onNoteDetected={onMicDetected}
          onWrongConfirmed={onWrong}
          onNoteMatched={onMicMatched}
          onComplete={()=>setGlobalMatched(TOTAL_NOTES)}
          detectionSensitivity={MIC_CFG.detectionSensitivity}
          toleranceCents={MIC_CFG.toleranceCents}
          toleranceHz={MIC_CFG.toleranceHz}
          wrongStableFramesRequired={MIC_CFG.wrongStableFrames}
          wrongCooldownMs={MIC_CFG.wrongCooldownMs}
          wrongCentsThreshold={MIC_CFG.wrongCentsThreshold}
          wrongHzThreshold={MIC_CFG.wrongHzThreshold}
        />
      </div>

      {/* Stop listening */}
      {listening&&!allDone&&(
        <div style={{position:"fixed",bottom:"calc(10px + env(safe-area-inset-bottom))",
          left:"50%",transform:"translateX(-50%)",
          zIndex:9998,width:"100%",maxWidth:360,padding:"0 14px"}}>
          <button type="button" onClick={toggleMic} style={{
            width:"100%",padding:"13px",borderRadius:100,
            background:"#1E293B",color:"white",border:"none",cursor:"pointer",
            fontFamily:"'Fredoka One',cursive",fontSize:"0.95rem",
            boxShadow:"0 8px 26px rgba(0,0,0,0.30)"}}>
            ⏹ Stop Listening
          </button>
        </div>
      )}

      <FinishModal
        open={showEnd} score={score} accuracy={accuracy}
        onClose={()=>setShowEnd(false)}
        goLevel1={()=>{ setTutorialDone(); go("/game-menu"); }}
        again={reset}
        learnNotes={()=>go("/learn-your-notes")}
        onShowMap={()=>{ setTutorialDone(); setShowEnd(false); setShowMap(true); }}
      />
      <TrebleTownMapModal open={showMap} onClose={()=>setShowMap(false)} />
    </div>
  );
} 